import axios from 'axios';
import {
  ReservationInput, PredictWeekResponse,
  Mitarbeiter, DienstplanSummary, DienstplanDetail,
  Urlaub, DashboardStats, LoginResponse,
  DayStaffingInput, GeneratePlanResponse,
} from './types';
import { PRODUCT } from '../config';
import { supabase } from '../lib/supabase';

// FastAPI error payloads: .detail is a string for HTTPException but an ARRAY of
// objects for 422 validation errors — rendering the latter crashes React (#31).
// Always go through this helper when showing an API error to the user.
export function apiErrorText(e: any): string | undefined {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x: any) => x?.msg || JSON.stringify(x)).join('; ');
  return undefined;
}


// Compute endpoints (generate-plan, predict-week, export-pdf) still live on Modal.
// In dev/Netlify the frontend is a different origin, so point at the Modal app URL
// via REACT_APP_API_URL; when Modal serves the bundle itself, leave it empty (same-origin).
const API_BASE = process.env.REACT_APP_API_URL || '';
const api = axios.create({
  baseURL: `${API_BASE}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
});

// Attach the live Supabase access token + X-Product header on every request.
api.interceptors.request.use(async (config) => {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers['X-Product'] = PRODUCT;
  return config;
});

// NOTE: during the Supabase migration the legacy /api/v1 backend does not yet
// accept Supabase JWTs (that lands in Phase 5), so a 401 here is expected for
// not-yet-migrated endpoints. Do NOT force a logout/redirect on 401 — that would
// bounce a validly signed-in Supabase user. Let each caller handle the error.
api.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error),
);

// Admin: Activity logs
export interface LogEntry {
  ts: string;
  user: string;
  method: string;
  path: string;
  status: number;
  duration_ms: number;
  error?: string;
  body?: string;
}

export interface LogResponse {
  entries: LogEntry[];
  stats: { total: number; errors: number; users: string[] };
}

export const fetchActivityLogs = async (
  limit: number = 500,
  userFilter?: string,
  onlyErrors: boolean = false,
): Promise<LogResponse> => {
  const params: any = { limit };
  if (userFilter) params.user_filter = userFilter;
  if (onlyErrors) params.only_errors = true;
  const res = await api.get<LogResponse>('/admin/logs', { params });
  return res.data;
};

export const clearActivityLogs = async (): Promise<void> => {
  await api.delete('/admin/logs');
};

// Admin analytics
export interface RestaurantStat {
  id: string;
  name: string;
  mitarbeiter_aktiv: number;
  mitarbeiter_gesamt: number;
  plaene_total: number;
  kosten_gesamt: number;
}
export interface WeeklyCost {
  kw: number;
  jahr: number;
  woche_start: string;
  standort_name: string;
  kosten: number;
  personal: number;
}
export interface EmployeeStat {
  id: string;
  name: string;
  standort_name: string;
  kategorie: string;
  wochenstunden: number;
  stundenlohn: number;
  aktiv: boolean;
}
export interface AnalyticsResponse {
  restaurants: RestaurantStat[];
  weekly_costs: WeeklyCost[];
  top_employees_by_hours: EmployeeStat[];
  total_mitarbeiter: number;
  total_aktive: number;
  total_monatskosten: number;
  total_jahreskosten: number;
}

export const fetchAnalytics = async (restaurantId?: string): Promise<AnalyticsResponse> => {
  const params = restaurantId ? { restaurant: restaurantId } : {};
  const res = await api.get<AnalyticsResponse>('/admin/analytics', { params });
  return res.data;
};

// Auth
export const login = async (username: string, password: string): Promise<LoginResponse> => {
  const res = await api.post<LoginResponse>('/auth/login', { username, password });
  return res.data;
};

// Predictions
export const predictWeek = async (weekStart: string, reservations: ReservationInput): Promise<PredictWeekResponse> => {
  const res = await api.post<PredictWeekResponse>('/predict-week', { week_start: weekStart, reservations });
  return res.data;
};

// Dashboard
export const fetchDashboardStats = async (): Promise<DashboardStats> => {
  const res = await api.get<DashboardStats>('/dashboard/stats');
  return res.data;
};

// Mitarbeiter
export const fetchMitarbeiter = async (): Promise<Mitarbeiter[]> => {
  const res = await api.get<Mitarbeiter[]>('/mitarbeiter');
  return res.data;
};

export const fetchMitarbeiterById = async (id: string): Promise<Mitarbeiter> => {
  const res = await api.get<Mitarbeiter>(`/mitarbeiter/${id}`);
  return res.data;
};

export const updateMitarbeiter = async (id: string, data: Partial<Mitarbeiter>): Promise<Mitarbeiter> => {
  const res = await api.put<Mitarbeiter>(`/mitarbeiter/${id}`, data);
  return res.data;
};

export const createMitarbeiter = async (data: {vorname: string; nachname: string; rolle: string; wochenstunden: number; stundenlohn: number}): Promise<Mitarbeiter> => {
  const res = await api.post<Mitarbeiter>('/mitarbeiter', data);
  return res.data;
};

export const deleteMitarbeiter = async (id: string): Promise<void> => {
  await api.delete(`/mitarbeiter/${id}`);
};

export const importMitarbeiterCSV = async (file: File): Promise<{imported: number; errors: string[]}> => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post('/mitarbeiter/csv-import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
};

// Dienstplaene
export const fetchDienstplaene = async (): Promise<DienstplanSummary[]> => {
  const res = await api.get<DienstplanSummary[]>('/dienstplaene');
  return res.data;
};

export const fetchDienstplanDetail = async (id: string): Promise<DienstplanDetail> => {
  const res = await api.get<DienstplanDetail>(`/dienstplaene/${id}`);
  return res.data;
};

// Update Schicht (edit employee names)
export const updateSchicht = async (schichtId: string, mitarbeiterNamen: string): Promise<any> => {
  const res = await api.put(`/dienstplaene/schicht/${schichtId}`, { mitarbeiter_namen: mitarbeiterNamen });
  return res.data;
};

export const updateDienstplanStatus = async (id: string, status: string): Promise<any> => {
  const res = await api.put(`/dienstplaene/${id}/status`, { status });
  return res.data;
};

export const deleteDienstplan = async (id: string): Promise<void> => {
  await api.delete(`/dienstplaene/${id}`);
};

// Feiertage
export interface Feiertag {
  id: string;
  name: string;
  datum: string;
  geschlossen: boolean;
}

export const fetchFeiertage = async (): Promise<Feiertag[]> => {
  const res = await api.get<Feiertag[]>('/feiertage');
  return res.data;
};

export const createFeiertag = async (data: { name: string; datum: string; geschlossen?: boolean }): Promise<Feiertag> => {
  const res = await api.post<Feiertag>('/feiertage', data);
  return res.data;
};

export const deleteFeiertag = async (id: string): Promise<void> => {
  await api.delete(`/feiertage/${id}`);
};

// Reservations (actual data for buffer calibration)
export interface ReservationsData {
  dienstplan_id: string;
  quelle: string;
  tatsaechlich_mittag: number[];
  tatsaechlich_abend: number[];
  geplant_mittag: number[];
  geplant_abend: number[];
}

export const fetchReservations = async (dienstplanId: string): Promise<ReservationsData> => {
  const res = await api.get<ReservationsData>(`/reservations/${dienstplanId}`);
  return res.data;
};

export const saveManualReservations = async (
  dienstplanId: string,
  mittag: number[],
  abend: number[],
): Promise<ReservationsData> => {
  const res = await api.post<ReservationsData>(`/reservations/${dienstplanId}`, { mittag, abend });
  return res.data;
};

export const uploadReservationCSV = async (
  dienstplanId: string,
  file: File,
): Promise<ReservationsData> => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post<ReservationsData>(`/reservations/${dienstplanId}/csv-upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
};

export const syncOpenTable = async (dienstplanId: string): Promise<ReservationsData> => {
  const res = await api.post<ReservationsData>(`/reservations/${dienstplanId}/sync-opentable`);
  return res.data;
};

// Calibration
export interface CalibrationDay {
  weekday: string;
  weekday_de: string;
  samples: number;
  avg_delta_mittag: number;
  avg_delta_abend: number;
  avg_total_delta: number;
  suggested_buffer: number;
}

export interface CalibrationData {
  samples_total: number;
  weeks_analyzed: number;
  per_weekday: CalibrationDay[];
  overall_avg_buffer: number;
  current_flat_buffer: number;
  potential_savings_pct: number;
}

export const fetchCalibration = async (): Promise<CalibrationData> => {
  const res = await api.get<CalibrationData>('/reservations/analytics/calibration');
  return res.data;
};

// Restaurant settings (admin only)
export interface RestaurantSettings {
  id: string;
  name: string;
  opentable_partner_id?: string;
  opentable_restaurant_id?: string;
  opentable_bearer_token_set: boolean;
  opentable_org_key_set: boolean;
}

export const fetchRestaurantSettings = async (): Promise<RestaurantSettings[]> => {
  const res = await api.get<RestaurantSettings[]>('/settings/restaurants');
  return res.data;
};

export const updateRestaurantSettings = async (
  id: string,
  data: {
    opentable_partner_id?: string;
    opentable_restaurant_id?: string;
    opentable_bearer_token?: string;
    opentable_org_key?: string;
  },
): Promise<RestaurantSettings> => {
  const res = await api.put<RestaurantSettings>(`/settings/restaurants/${id}`, data);
  return res.data;
};

// Urlaube
export const fetchUrlaube = async (status?: string): Promise<Urlaub[]> => {
  const params = status ? { status } : {};
  const res = await api.get<Urlaub[]>('/urlaube', { params });
  return res.data;
};

export const createUrlaub = async (data: { mitarbeiter_id: string; von_datum: string; bis_datum: string; typ?: string }): Promise<Urlaub> => {
  const res = await api.post<Urlaub>('/urlaube', data);
  return res.data;
};

export const updateUrlaubStatus = async (id: string, status: string): Promise<Urlaub> => {
  const res = await api.put<Urlaub>(`/urlaube/${id}/status`, { status });
  return res.data;
};

// Export PDF
export const exportPDF = async (pdfData: any): Promise<Blob> => {
  const res = await api.post('/export-pdf', pdfData, { responseType: 'blob' });
  return res.data;
};

// Generate Plan
export const generatePlan = async (
  weekStart: string,
  staffing: Record<string, DayStaffingInput>,
): Promise<GeneratePlanResponse> => {
  const res = await api.post<GeneratePlanResponse>('/generate-plan', {
    week_start: weekStart,
    staffing,
  });
  return res.data;
};

// ----------------------------- Invoices / Wareneinsatz ------------------------

export interface InvoiceSummary {
  id: number;
  supplier: string;
  supplier_format: string;
  invoice_no?: string | null;
  invoice_date?: string | null;
  total_net?: number | null;
  currency: string;
  source_file?: string | null;
  needs_ocr: boolean;
  line_count: number;
  alert_count: number;
  created_at: string;
}

export interface InvoiceLine {
  id: number;
  supplier: string;
  article_no: string;
  name: string;
  quantity: number;
  unit: string;
  unit_price: number;
  total: number;
  vat_pct?: number | null;
  invoice_date?: string | null;
}

export interface PriceAlert {
  id?: number;
  invoice_line_id?: number;
  supplier: string;
  article_no: string;
  article_name: string;
  current_price: number;
  best_price: number;
  best_price_date?: string | null;
  delta_pct: number;
  yearly_impact_eur: number;
  window_weeks: number;
  status: string;
  invoice_id?: number | null;
  invoice_date?: string | null;
  alert_type?: string;                       // cross_supplier | same_supplier
  best_supplier?: string | null;
  cluster_id?: string | null;
  cluster_name?: string | null;
  normalized_unit?: string | null;
  retro_overpaid_eur?: number | null;
  retro_qty?: number | null;
  retro_from?: string | null;
  retro_to?: string | null;
  supplier_total_spend_eur?: number | null;
}

export interface InvoiceDetail extends InvoiceSummary {
  lines: InvoiceLine[];
  alerts: PriceAlert[];
}

export interface InvoiceStats {
  invoice_count: number;
  line_count: number;
  supplier_count: number;
  article_count: number;
  open_alerts: number;
  yearly_savings_potential: number;
  suppliers: { supplier: string; invoice_count: number }[];
}

export const uploadInvoice = async (
  file: File,
  windowWeeks: number = 12,
  thresholdPct: number = 1.0,
): Promise<{ invoice: InvoiceDetail; new_alerts: PriceAlert[] }> => {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post('/invoices/upload', form, {
    params: { window_weeks: windowWeeks, threshold_pct: thresholdPct },
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
};

export const fetchInvoices = async (limit = 100): Promise<InvoiceSummary[]> => {
  const res = await api.get<InvoiceSummary[]>('/invoices', { params: { limit } });
  return res.data;
};

export const fetchInvoiceDetail = async (id: number): Promise<InvoiceDetail> => {
  const res = await api.get<InvoiceDetail>(`/invoices/${id}`);
  return res.data;
};

export const fetchAlerts = async (status = 'open', limit = 200): Promise<PriceAlert[]> => {
  const res = await api.get<PriceAlert[]>('/invoices/alerts', { params: { status, limit } });
  return res.data;
};

export const updateAlertStatus = async (id: number, status: string): Promise<PriceAlert> => {
  const res = await api.patch<PriceAlert>(`/invoices/alerts/${id}`, { status });
  return res.data;
};

export const generateAlertWhatsApp = async (
  id: number,
  phone: string = '',
): Promise<{ message: string; phone_url: string }> => {
  const res = await api.post(`/invoices/alerts/${id}/whatsapp`, null, { params: { phone } });
  return res.data;
};

export const fetchArticleHistory = async (
  supplier: string,
  articleNo: string,
): Promise<{
  supplier: string;
  article_no: string;
  name: string;
  unit: string;
  points: { date: string | null; unit_price: number; quantity: number; invoice_id: number }[];
}> => {
  const res = await api.get('/invoices/article-history', {
    params: { supplier, article_no: articleNo },
  });
  return res.data;
};

export const fetchInvoiceStats = async (): Promise<InvoiceStats> => {
  const res = await api.get<InvoiceStats>('/invoices/stats/summary');
  return res.data;
};

// ----------------------------- Mailboxes / IMAP -------------------------------

export interface Mailbox {
  id: number;
  name: string;
  host: string;
  port: number;
  use_ssl: boolean;
  username: string;
  password_set: boolean;
  folder: string;
  poll_interval_min: number;
  is_active: boolean;
  last_polled_at?: string | null;
  last_uid?: string | null;
  last_error?: string | null;
  created_at: string;
}

export interface IncomingEmail {
  id: number;
  mailbox_id?: number | null;
  message_id?: string | null;
  uid?: string | null;
  from_addr?: string | null;
  subject?: string | null;
  received_at?: string | null;
  attachment_count: number;
  processed_count: number;
  skipped_count: number;
  failed_count: number;
  status: string;
  error?: string | null;
  created_at: string;
}

export interface MailboxIn {
  name: string;
  host: string;
  port: number;
  use_ssl: boolean;
  username: string;
  password?: string;
  folder: string;
  poll_interval_min: number;
  is_active: boolean;
}

export const fetchMailboxes = async (): Promise<Mailbox[]> => {
  const res = await api.get<Mailbox[]>('/mailboxes');
  return res.data;
};

export const createMailbox = async (data: MailboxIn): Promise<Mailbox> => {
  const res = await api.post<Mailbox>('/mailboxes', data);
  return res.data;
};

export const updateMailbox = async (id: number, data: MailboxIn): Promise<Mailbox> => {
  const res = await api.patch<Mailbox>(`/mailboxes/${id}`, data);
  return res.data;
};

export const deleteMailbox = async (id: number): Promise<void> => {
  await api.delete(`/mailboxes/${id}`);
};

export const pollMailboxNow = async (id: number): Promise<void> => {
  await api.post(`/mailboxes/${id}/poll`);
};

export const pollAllMailboxes = async (): Promise<void> => {
  await api.post('/mailboxes/poll-all');
};

export const fetchIncomingEmails = async (limit = 200): Promise<IncomingEmail[]> => {
  const res = await api.get<IncomingEmail[]>('/mailboxes/inbox', { params: { limit } });
  return res.data;
};

// ----------------------------- Articles (Search & Compare) --------------------

export interface SupplierInfo {
  name: string;
  // backend may return more fields; we only need `name` here
  [key: string]: any;
}

export interface ArticleListItem {
  supplier: string;
  article_no: string;
  name: string;
  unit: string;
  last_unit_price: number;
  min_unit_price_90d: number;
  max_unit_price_90d: number;
  last_seen: string | null;
  line_count: number;
  total_spend: number;
  open_alerts: number;
  delta_pct: number;
}

export interface ArticleListResponse {
  total: number;
  page: number;
  page_size: number;
  items: ArticleListItem[];
}

// Provenance of a single invoice line: how the €/Einheit was derived.
// Shared by the article-history points (admin "Artikel" page + owner dashboard),
// the outlier review and the "nicht normalisiert" review.
export interface LineProvenance {
  line_id?: number | null;
  invoice_id?: number | null;
  invoice_no?: string | null;
  unit?: string | null;                     // printed unit on the invoice (KT, KG, ST …)
  total?: number | null;                    // printed line total (€)
  normalized_unit?: string | null;          // kg | l | Stück
  base_quantity?: number | null;            // how many normalized units the line covers
  unit_price_per_einheit?: number | null;   // total ÷ base_quantity; null = nicht normalisiert
  pack_method?: string | null;              // e.g. rule:nxv-std, llm, manual
  pack_confidence?: number | null;          // 0..1
  raw?: string | null;                      // raw invoice line text
}

// NOTE: `ArticleHistoryPoint` is declared twice in this file (here and in the
// owner-dashboard section) and TypeScript merges both declarations. Fields that
// exist in both shapes live once in `LineProvenance` to keep the merge conflict-free.
export interface ArticleHistoryPoint extends LineProvenance {
  date: string;
  unit_price: number;
  quantity: number;
  invoice_id: number;
}

export interface ArticleDetailAlert {
  id: number;
  current_price: number;
  best_price: number;
  best_price_date: string | null;
  delta_pct: number;
  yearly_impact_eur: number;
  status: string;
}

export interface ArticleDetail {
  supplier: string;
  article_no: string;
  name: string;
  unit: string;
  kpi: {
    current_price: number;
    min_90d: number;
    max_90d: number;
    avg_90d: number;
    total_spend: number;
    invoice_count: number;
  };
  history: ArticleHistoryPoint[];
  // Backend field is `open_alerts` (a list); keep the name aligned to avoid a
  // silent undefined that crashes the detail dialog.
  open_alerts: ArticleDetailAlert[];
}

export const fetchSuppliers = async (): Promise<SupplierInfo[]> => {
  const res = await api.get<SupplierInfo[]>('/suppliers');
  return res.data;
};

export const fetchArticles = async (params: {
  q?: string;
  suppliers?: string;
  date_from?: string;
  date_to?: string;
  only_alerts?: boolean;
  sort?: string;
  page?: number;
  page_size?: number;
}): Promise<ArticleListResponse> => {
  const res = await api.get<ArticleListResponse>('/articles', { params });
  return res.data;
};

export const fetchArticleDetail = async (
  supplier: string,
  articleNo: string,
): Promise<ArticleDetail> => {
  const res = await api.get<ArticleDetail>('/articles/detail', {
    params: { supplier, article_no: articleNo },
  });
  return res.data;
};

// ----------------------------- Suppliers (Lieferanten overview) ---------------

export interface SupplierSummary {
  name: string;
  invoice_count: number;
  line_count: number;
  article_count: number;
  total_spend_eur: number;
  last_invoice_date: string | null;
  open_alerts: number;
  avg_overcharge_pct: number | null;
}

export interface SupplierTopArticle {
  article_no: string;
  name: string;
  last_unit_price: number | null;
  total_spend: number;
  line_count: number;
}

export interface SupplierRecentInvoice {
  id: number;
  invoice_no: string | null;
  invoice_date: string | null;
  total_net: number | null;
  line_count: number;
}

export interface SupplierDetail extends SupplierSummary {
  top_articles: SupplierTopArticle[];
  recent_invoices: SupplierRecentInvoice[];
}

export const fetchSupplierSummaries = async (
  dateFrom?: string,
  dateTo?: string,
): Promise<SupplierSummary[]> => {
  const params: any = {};
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  const res = await api.get<SupplierSummary[]>('/suppliers', { params });
  return res.data;
};

export const fetchSupplierDetail = async (name: string): Promise<SupplierDetail> => {
  const res = await api.get<SupplierDetail>('/suppliers/detail', { params: { name } });
  return res.data;
};

// ----------------------------- Owner Dashboard --------------------------------

export interface DashboardFilters {
  date_from?: string;
  date_to?: string;
  suppliers?: string;  // CSV
  categories?: string; // CSV
}

export interface DashboardKpis {
  sparpotenzial_eur_yearly: number;
  retro_overpaid_eur?: number;
  total_spend_eur: number;
  avg_overcharge_pct: number;
  open_alerts: number;
  articles_affected: number;
  active_suppliers: number;
  invoices: number;
  invoice_lines: number;
  classified_articles: number;
  cross_supplier_clusters: number;
}

export interface MoverSparklinePoint { date: string; unit_price_per_einheit: number; }
export interface Mover {
  cluster_id?: string | null;
  cluster_name: string;
  supplier: string;
  article_no: string;
  name_sample: string;
  category?: string | null;
  normalized_unit?: string | null;
  latest_price: number;
  baseline_price: number;
  delta_pct: number;
  direction: 'up' | 'down';
  estimated_yearly_impact: number;
  sparkline: MoverSparklinePoint[];
}
export interface TopMovers { risers: Mover[]; fallers: Mover[]; window_days: number; }

// Merges with the declaration above (see note there). Provenance fields
// (line_id, invoice_no, unit, normalized_unit, pack_method, pack_confidence,
// raw, base_quantity, unit_price_per_einheit) come from `LineProvenance`.
export interface ArticleHistoryPoint {
  date: string;
  unit_price: number;
  quantity: number;
  quantity_normalized: number;
  inhalt_count?: number | null;
  total_eur: number;
  invoice_id: number;
  supplier: string;
  article_name?: string | null;
}
export interface DashboardArticleDetail {
  cluster_id?: string | null;
  cluster_name: string;
  category?: string | null;
  normalized_unit?: string | null;
  is_tagespreis: boolean;
  current_price_min: number;
  current_price_max: number;
  avg_price: number;
  median_price: number;
  min_ever_price: number;
  min_ever_supplier?: string | null;
  min_ever_date?: string | null;
  total_spend_window: number;
  total_qty_normalized: number;
  would_have_saved: number;
  invoice_count: number;
  supplier_count: number;
  history: ArticleHistoryPoint[];
}

export interface SupplierScoreRow {
  supplier: string;
  total_spend: number;
  share_of_spend_pct: number;
  avg_overcharge_pct: number;
  open_alerts: number;
  invoice_count: number;
  line_count: number;
  article_count: number;
  last_invoice_date?: string | null;
}

export interface CategoryRow {
  category: string;
  total_spend: number;
  share_pct: number;
  line_count: number;
  article_count: number;
  avg_overcharge_pct: number;
  estimated_savings: number;
}

export interface SpendBucket {
  period_start: string;
  total_spend: number;
  invoice_count: number;
  estimated_savings: number;
}

export interface Anomaly {
  type: string;
  severity: 'info' | 'warn' | 'critical';
  title: string;
  detail: string;
  cluster_id?: string | null;
  supplier?: string | null;
  article_no?: string | null;
  related_eur: number;
}

export interface CorridorBucket {
  week_start: string;
  min_price: number;
  max_price: number;
  median_price: number;
  line_count: number;
}
export interface Corridor {
  cluster_id: string;
  cluster_name: string;
  normalized_unit?: string | null;
  is_tagespreis: boolean;
  buckets: CorridorBucket[];
}

export interface DashboardFilterOptions {
  suppliers: string[];
  categories: string[];
}

const dashGet = async <T>(path: string, params: any = {}): Promise<T> => {
  const res = await api.get<T>(path, { params });
  return res.data;
};

export const fetchDashboardFilters = () =>
  dashGet<DashboardFilterOptions>('/owner-dashboard/filters');

export const fetchDashboardKpis = (f: DashboardFilters = {}) =>
  dashGet<DashboardKpis>('/owner-dashboard/kpis', f);

export const fetchTopMovers = (f: DashboardFilters & { limit?: number; window_days?: number } = {}) =>
  dashGet<TopMovers>('/owner-dashboard/top-movers', f);

export const fetchDashboardArticleDetail = (params: {
  cluster_id?: string; supplier?: string; article_no?: string;
  date_from?: string; date_to?: string;
}) => dashGet<DashboardArticleDetail>('/owner-dashboard/article-detail', params);

export const fetchSupplierScorecard = (f: DashboardFilters = {}) =>
  dashGet<SupplierScoreRow[]>('/owner-dashboard/supplier-scorecard', f);

export const fetchCategoryBreakdown = (f: DashboardFilters = {}) =>
  dashGet<CategoryRow[]>('/owner-dashboard/category-breakdown', f);

export const fetchSpendTimeline = (f: DashboardFilters & { bucket?: 'week' | 'month' } = {}) =>
  dashGet<SpendBucket[]>('/owner-dashboard/spend-timeline', f);

export const fetchAnomalies = (f: DashboardFilters = {}) =>
  dashGet<Anomaly[]>('/owner-dashboard/anomalies', f);

export const fetchTagespreisCorridor = (params: {
  cluster_id: string; date_from?: string; date_to?: string;
}) => dashGet<Corridor>('/owner-dashboard/tagespreis-corridor', params);

// ----------------------------- Koch Search ------------------------------------

export interface KochResult {
  cluster_id?: string | null;
  cluster_name: string;
  article_name: string;
  article_no: string;
  supplier: string;
  category?: string | null;
  normalized_unit?: string | null;
  unit_price_per_einheit?: number | null;
  last_quantity: number;
  last_seen?: string | null;
  is_bestpreis: boolean;
  pct_vs_bestpreis: number;
}

export interface KochSearchResponse {
  query: string;
  total_matches: number;
  results: KochResult[];
}

export const kochSearch = async (q: string, limit = 30): Promise<KochSearchResponse> => {
  const res = await api.get<KochSearchResponse>('/koch/search', { params: { q, limit } });
  return res.data;
};

// ----------------------------- Price review (outlier correction) --------------

export interface OutlierLine {
  line_id: number;
  supplier: string;
  article_no: string;
  name: string;
  cluster_name?: string | null;
  normalized_unit?: string | null;
  unit_price_per_einheit?: number | null;
  cluster_median: number;
  ratio: number;
  quantity: number;
  unit: string;
  unit_price: number;
  total: number;
  invoice_date?: string | null;
  // Provenance (newer backend; old rows may lack these)
  invoice_id?: number | null;
  invoice_no?: string | null;
  base_quantity?: number | null;
  pack_method?: string | null;
  pack_confidence?: number | null;
  raw?: string | null;
}

export const fetchPriceOutliers = async (factor = 4): Promise<OutlierLine[]> => {
  const res = await api.get<OutlierLine[]>('/invoices/lines/outliers', { params: { factor } });
  return res.data;
};

// Lines whose pack size could not be read confidently → no €/Einheit yet.
// Excluded from every comparison until a human fixes them.
export interface UnnormalizedLine {
  line_id: number;
  invoice_id: number;
  invoice_no?: string | null;
  invoice_date?: string | null;
  supplier: string;
  article_no: string;
  name: string;
  cluster_name?: string | null;
  quantity: number;
  unit: string;
  unit_price: number;
  total: number;
  normalized_unit?: string | null;
  pack_method?: string | null;
  pack_confidence?: number | null;
  raw?: string | null;
}

export const fetchUnnormalizedLines = async (limit = 300): Promise<UnnormalizedLine[]> => {
  const res = await api.get<UnnormalizedLine[]>('/invoices/lines/unnormalized', { params: { limit } });
  return res.data;
};

// Opens the stored original PDF of an invoice in a new tab. The endpoint needs
// the auth header, so we fetch the bytes and open a blob URL instead of a plain
// <a href>. Rejects with an axios error (404 = no PDF stored) — callers show a notice.
export const openInvoicePdf = async (invoiceId: number): Promise<void> => {
  // Mobile browsers block window.open after an async fetch (the click gesture is
  // gone by then). Open an empty tab synchronously INSIDE the gesture, then
  // navigate it; if even that is blocked, open the PDF in the current tab.
  const w = window.open('', '_blank');
  try {
    const res = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    if (w && !w.closed) {
      w.location.href = url;
    } else {
      window.location.href = url;
    }
  } catch (e) {
    if (w && !w.closed) w.close();
    throw e;
  }
};

export const correctLineNormalization = async (
  lineId: number, unitPricePerEinheit: number, applyToArticle = true, normalizedUnit?: string,
): Promise<{ ok: boolean; lines_updated: number; inhalt_count: number }> => {
  const body: { unit_price_per_einheit: number; apply_to_article: boolean; normalized_unit?: string } = {
    unit_price_per_einheit: unitPricePerEinheit, apply_to_article: applyToArticle,
  };
  if (normalizedUnit) body.normalized_unit = normalizedUnit;
  const res = await api.patch(`/invoices/lines/${lineId}/normalization`, body);
  return res.data;
};

export const recomputeAlertsNow = async (): Promise<{ ok: boolean; alerts: number; sparpotenzial_eur_yearly: number }> => {
  const res = await api.post('/invoices/alerts/recompute');
  return res.data;
};

// ----------------------------- Sender triage (Lieferanten-Prüfung) ------------
// The AI learns, per sender-domain and per invoice-issuer, whether an incoming
// mail is from a goods supplier whose prices Skonto should track. This surfaces
// those learned verdicts for human review + override.

export interface GraphStatus {
  configured: boolean;
  mailbox: string | null;
  poll_interval_min: number;
}

export interface SenderVerdict {
  id: number;
  kind: 'domain' | 'issuer';
  key_value: string;
  display_name: string | null;
  is_supplier: boolean;
  category: string | null;
  reason: string | null;
  confidence: number | null;
  source: string;          // 'ai' | 'manual'
  times_seen: number;
  last_seen: string | null;
}

export const fetchGraphStatus = async (): Promise<GraphStatus> => {
  const res = await api.get<GraphStatus>('/graph/status');
  return res.data;
};

export const triggerGraphPoll = async (): Promise<{ ok: boolean; started: boolean }> => {
  const res = await api.post<{ ok: boolean; started: boolean }>('/graph/poll');
  return res.data;
};

export const fetchVerdicts = async (): Promise<SenderVerdict[]> => {
  const res = await api.get<SenderVerdict[]>('/graph/verdicts');
  return res.data;
};

export const overrideVerdict = async (
  id: number,
  isSupplier: boolean,
): Promise<{ ok: boolean; is_supplier: boolean }> => {
  const res = await api.patch<{ ok: boolean; is_supplier: boolean }>(
    `/graph/verdicts/${id}`,
    { is_supplier: isSupplier },
  );
  return res.data;
};

// ----------------------------- Feedback ---------------------------------------

export interface FeedbackEntry {
  id: number;
  created_at: string;
  user_email?: string | null;
  role?: string | null;
  page?: string | null;
  message: string;
  status: string;
}

export const submitFeedback = async (message: string, page?: string): Promise<void> => {
  await api.post('/feedback', { message, page });
};

export const fetchFeedback = async (status: 'offen' | 'erledigt' | 'alle' = 'offen'): Promise<FeedbackEntry[]> => {
  const res = await api.get('/feedback', { params: { status } });
  return res.data;
};

export const updateFeedbackStatus = async (id: number, status: 'offen' | 'erledigt'): Promise<void> => {
  await api.patch(`/feedback/${id}`, { status });
};

// --------------------- Lieferanten-WhatsApp-Kontakte ---------------------------

export const fetchSupplierContacts = async (): Promise<Record<string, string>> => {
  const res = await api.get('/supplier-contacts');
  return res.data || {};
};

export const saveSupplierContact = async (supplier: string, phone: string): Promise<void> => {
  await api.put('/supplier-contacts', { supplier, phone });
};
